<script lang="ts" setup>
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { isEmail } from '@@/bits/email'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import { __, p__ } from '@@/bits/intl'
import { buildUrlFromPath, goBack, navigateTo, transformCurrentUrl, transformUrl } from '@@/bits/location'
import OzBackButton from '@@/library/v4/components/OzBackButton.vue'
import OzCheckBox from '@@/library/v4/components/OzCheckBox.vue'
import OzContainedButton, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzContainedButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzInput from '@@/library/v4/components/OzInput.vue'
import OzLabelInput from '@@/library/v4/components/OzLabelInput.vue'
import OzLoadingText from '@@/library/v4/components/OzLoadingText.vue'
import OzPadletLogo from '@@/library/v4/components/OzPadletLogo.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import OzTextLink from '@@/library/v4/components/OzTextLink.vue'
import { useAuthLoginStore } from '@@/pinia/auth_login_store'
import { useWindowSizeStore } from '@@/pinia/window_size'
import AuthActionMenu from '@@/vuecomponents/AuthActionMenu.vue'
import AuthBottomNav from '@@/vuecomponents/AuthBottomNav.vue'
import AuthLoginSAMLButton from '@@/vuecomponents/AuthLoginSAMLButton.vue'
import AuthLoginThirdPartyGroupButtons, {
  AuthLoginThirdPartyGroupButtonsType,
} from '@@/vuecomponents/AuthLoginThirdPartyGroupButtons.vue'
import CachedAuthLoginSAMLButton from '@@/vuecomponents/CachedAuthLoginSAMLButton.vue'
import CachedAuthLoginThirdPartyGroupButtons from '@@/vuecomponents/CachedAuthLoginThirdPartyGroupButtons.vue'
import GlobalSnackbar from '@@/vuecomponents/GlobalSnackbar.vue'
import SkipToContentLink from '@@/vuecomponents/SkipToContentLink.vue'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

const windowSizeStore = useWindowSizeStore()
const authLoginStore = useAuthLoginStore()

const { isSmallerThanTabletPortrait } = storeToRefs(windowSizeStore)
const {
  username,
  password,
  usernameValidationMessage,
  passwordValidationMessage,
  isAwaitingServerResponse,
  thirdPartyAuthLoginMethods,
  userThirdPartyAuthLoginMethods,
  samlLoginMethods,
  isUsernameVerified,
  isUsernameValid,
  isPasswordValid,
  codeChallenge,
  codeChallengeMethod,
  pkceState,
  originAppClientId,
  originAppRedirectUri,
  isPkceEnabled,
  isPasswordLoginEnabled,
  isSchoolLibraryLoginPage,
  schoolLibraryName,
} = storeToRefs(authLoginStore)
const {
  setUsername,
  setUsernameValidationMessage,
  setPassword,
  setPasswordValidationMessage,
  setIsUsernameVerified,
  continueWithEmailOrUsername,
  loginWithPassword,
  resetIsAwaitingServerResponse,
} = authLoginStore

const usernameInputRef = ref<InstanceType<typeof OzInput>>()
const passwordInputRef = ref<InstanceType<typeof OzInput>>()
const xActionMenu = ref(false)
const isUsernameInputFocused = ref(false)
const isPasswordInputFocused = ref(false)
const isPasswordShown = ref(false)
const prefilledPasswordByBrowser = ref('')

// Header elements are hidden when the input is focused on android phones.
const xHeader = computed((): boolean => {
  return !(
    device.mobile &&
    device.android &&
    isSmallerThanTabletPortrait.value &&
    (isUsernameInputFocused.value || isPasswordInputFocused.value)
  )
})

const isContinueButtonDisabled = computed((): boolean => {
  return !username.value.length || !!usernameValidationMessage.value.length
})

const isLoginButtonDisabled = computed((): boolean => {
  return !password.value.length || !!passwordValidationMessage.value.length
})

const signUpPageUrl = computed((): string => {
  const baseSignupUrl = buildUrlFromPath('/auth/signup')
  if (isPkceEnabled.value) {
    const signupUrlWithPkceParams = transformUrl(baseSignupUrl, {
      searchParams: {
        code_challenge: codeChallenge.value as string,
        code_challenge_method: codeChallengeMethod.value as string,
        redirect_uri: encodeURIComponent(originAppRedirectUri.value as string),
        client_id: originAppClientId.value as string,
        pkce_state: pkceState.value as string,
      },
    })
    return signupUrlWithPkceParams
  } else {
    return baseSignupUrl
  }
})

const orgDomainVerificationPageUrl = computed((): string => {
  const baseOrgDomainVerificationPageUrl = buildUrlFromPath('/auth/org_login_redirect')
  if (isPkceEnabled.value) {
    const orgDomainVerificationPageUrlWithPkceParams = transformUrl(baseOrgDomainVerificationPageUrl, {
      searchParams: {
        code_challenge: codeChallenge.value as string,
        code_challenge_method: codeChallengeMethod.value as string,
        redirect_uri: encodeURIComponent(originAppRedirectUri.value as string),
        client_id: originAppClientId.value as string,
        pkce_state: pkceState.value as string,
      },
    })
    return orgDomainVerificationPageUrlWithPkceParams
  } else {
    return baseOrgDomainVerificationPageUrl
  }
})

const passwordScreenHeading = computed((): string => {
  return isEmail(username.value) ? __('Log in with email') : __('Log in with username')
})

const forgotPasswordUrl = computed<string>(() => {
  if (isEmail(username.value)) {
    return transformCurrentUrl({}, { path: '/auth/forgot', search: { email: username.value } })
  }
  return transformCurrentUrl({}, { path: '/auth/forgot' })
})

const xUsernameVerificationScreen = computed((): boolean => {
  return !isUsernameVerified.value
})

const referredFromCreateSchoolLibrary = computed((): boolean => {
  const urlParams: URLSearchParams = new URLSearchParams(window.location.search)
  return urlParams.get('referrer')?.includes('school-library/create') || false
})

watch(
  xUsernameVerificationScreen,
  (newValue: boolean) => {
    // Only focus on current screen input field after a 1ms `setTimeout`,
    // because we need to wait for the input field to be shown in the DOM,
    // before we can focus on it.
    setTimeout(() => {
      nextTick(() => {
        if (newValue) {
          usernameInputRef.value?.focusInput()
        } else {
          passwordInputRef.value?.focusInput()
        }
      })
    }, 1)
  },
  { immediate: true },
)

function continueWithSamlText(methodName: string | undefined): string {
  return p__(
    'Text shown on SAML login button. SAML is a custom login method hosted by the university.',
    'Continue with %{samlLoginMethod}',
    { samlLoginMethod: methodName ?? 'SAML' },
  )
}

watch(isAwaitingServerResponse, (value: boolean) => {
  const html = document.getElementsByTagName('html')[0]

  if (xUsernameVerificationScreen.value) return

  // Animate the background color while waiting for the response
  // Note: Only do this when submitting the login form
  if (value) {
    html.classList.add('dot-grid-loading')
  } else {
    html.classList.remove('dot-grid-loading')
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('popstate', goBackEmailVerificationScreen)
})

function onInputUsernameInput(e): void {
  // Prevent the input from being refilled by browser after clicking the Continue button
  if (!xUsernameVerificationScreen.value) return

  setUsername(e.target.value)

  // Reset the username validation message to enable the Continue button when typing on mobile and tablet
  if (usernameValidationMessage.value.length) setUsernameValidationMessage('')
}

function onInputPasswordInput(e): void {
  setPassword(prefilledPasswordByBrowser.value || e.target.value)

  // Prevent the value of password input from always being prefilledPasswordByBrowser
  if (prefilledPasswordByBrowser.value) {
    prefilledPasswordByBrowser.value = ''
  }

  // Reset the password validation message to enable the Login button when typing on mobile and tablet
  if (passwordValidationMessage.value.length) setPasswordValidationMessage('')
}

function onFocusInUsernameInput(): void {
  isUsernameInputFocused.value = true
}

function onFocusInPasswordInput(): void {
  isPasswordInputFocused.value = true
}

function onBlurUsernameInput(): void {
  isUsernameInputFocused.value = false
}

function onBlurPasswordInput(): void {
  isPasswordInputFocused.value = false
}

function goBackEmailVerificationScreen(event: PopStateEvent | Event): void {
  // Go back to the email verification screen when clicking the back button on the password page
  // Assuming the login flow is:
  // (1) Visit home page → (2) Click Log in button → (3) Navigate to login page → (4) Enter email and click the Continue button
  // → (5) Navigate to the password page → (6) Click the back button on the page → (7) Navigate back to the email page → (8) Click the browser back button.
  // If we don't call `goBack()`, we have to do the step (8) twice to go back to the home page at step (1).
  if (event.type === 'click') {
    goBack()
    return
  }

  if (document.referrer !== '') {
    // Reset state only if referrer is not from Dashboard Settings delete page
    const deletePagePathnames = ['/dashboard/settings/library_delete', '/dashboard/settings/delete']
    const referrer = new URL(document.referrer)
    const isReferredFromDeletePage = deletePagePathnames.includes(referrer.pathname)
    if (isReferredFromDeletePage) return
  }

  isPasswordShown.value = false
  prefilledPasswordByBrowser.value = ''
  setIsUsernameVerified(false)
  setPassword('')
  setPasswordValidationMessage('')
  resetIsAwaitingServerResponse()
}

async function submit(): Promise<void> {
  if (xUsernameVerificationScreen.value) {
    // Blur the username input to prevent modifying the input value when submitting the form by clicking "Enter" key
    usernameInputRef.value?.blurInput()

    await continueWithEmailOrUsername()

    // Re-focus the username input if there is an error message
    if (usernameValidationMessage.value.length) {
      usernameInputRef.value?.focusInput()
    }

    // Browser refills username and password input after clicking the Continue button
    // so we need to store the password of the account that the user selected
    if (password.value) {
      prefilledPasswordByBrowser.value = password.value
    }

    if (isUsernameVerified.value && !window.history.state?.didPushState) {
      // Enable the browser back button on login page
      // didPushState: true to prevent pushing redundant states
      window.history.pushState({ didPushState: true }, '', window.location.href)
    }

    window.addEventListener('popstate', goBackEmailVerificationScreen)
  } else {
    // Blur the password input to prevent modifying the input value when submitting the form by clicking "Enter" key
    passwordInputRef.value?.blurInput()

    await loginWithPassword()

    // Re-focus the password input if there is an error message
    if (passwordValidationMessage.value.length) {
      passwordInputRef.value?.focusInput()
    }
  }
}

const loginText = computed<string>(() => {
  if (xUsernameVerificationScreen.value) {
    if (isSchoolLibraryLoginPage.value && schoolLibraryName.value !== null) {
      return __('Log in to %{schoolName}', { schoolName: schoolLibraryName.value })
    }
    return __('Log in')
  }
  return passwordScreenHeading.value
})
</script>

<template>
  <div class="relative flex flex-col items-center min-h-full">
    <header
      v-show="xHeader"
      :class="[
        'flex items-center justify-between box-border w-full px-4 transition-all',
        xUsernameVerificationScreen || (device.desktop && !isSmallerThanTabletPortrait)
          ? 'h-14 ps-5'
          : 'h-18 tablet-portrait:ps-6',
      ]"
    >
      <SkipToContentLink />

      <!-- UI for the email verification screen -->
      <template v-if="xUsernameVerificationScreen">
        <OzPadletLogo :class="[{ invisible: device.electronApp }]" />

        <div class="flex items-center text-body-small">
          <template v-if="device.desktop && !isSmallerThanTabletPortrait">
            <span class="text-dark-text-200 dark:text-light-text-200">
              {{ __('Not a member?') }}
            </span>

            <OzTextLink :href="signUpPageUrl" :text="__('Sign up')" class="font-semibold ms-1 me-2" />
          </template>
          <template v-else>
            <OzTextLink :href="signUpPageUrl" :text="__('Sign up')" class="font-semibold me-2" />

            <OzPlainButton
              :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
              :size-preset="OzPlainButtonSizePreset.H28px"
              :aria-label="__('Open more menu dialog')"
              data-testid="actionMenuButton"
              @click="xActionMenu = true"
            >
              <OzIcon :size="20" name="more_vertical" />
            </OzPlainButton>
          </template>
        </div>
      </template>

      <!-- UI for the login with password screen -->
      <template v-else>
        <OzPadletLogo
          v-if="device.desktop && !isSmallerThanTabletPortrait"
          :class="[{ invisible: device.electronApp }]"
        />

        <template v-else>
          <OzBackButton
            data-testid="landsideLoginBackButton"
            :disabled="isAwaitingServerResponse"
            @click="goBackEmailVerificationScreen"
          />

          <OzPlainButton
            v-if="device.tablet"
            :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
            :size-preset="OzPlainButtonSizePreset.H28px"
            :aria-label="__('Open more menu dialog')"
            @click="xActionMenu = true"
          >
            <OzIcon :size="20" name="more_vertical" />
          </OzPlainButton>
        </template>
      </template>
    </header>

    <main
      id="main-content"
      :class="[
        'h-full',
        'grow tablet-portrait:grow-0',
        'flex',
        'flex-col',
        'justify-end tablet-portrait:justify-center',
        'box-border',
        'w-full',
        'p-4',
        'tablet-portrait:relative tablet-portrait:w-[375px] tablet-portrait:p-0 tablet-portrait:px-4 tablet-portrait:mt-18',
      ]"
      tabindex="-1"
    >
      <!-- Only display the back button on the log in with password screen -->
      <OzBackButton
        v-if="!xUsernameVerificationScreen && device.desktop && !isSmallerThanTabletPortrait"
        data-testid="landsideLoginBackButton"
        :disabled="isAwaitingServerResponse"
        class="mb-5"
        @click="goBackEmailVerificationScreen"
      />

      <div class="text-dark-text-100 dark:text-light-text-100 mb-5">
        <h1 class="w-80 text-login-heading-1 font-semibold px-2 mb-1">
          {{ loginText }}
        </h1>
        <h2 class="text-login-body px-2">
          {{ xUsernameVerificationScreen ? __('Welcome back to where you belong') : username }}
        </h2>
      </div>

      <template v-if="samlLoginMethods !== null && samlLoginMethods.length > 0">
        <CachedAuthLoginSAMLButton v-if="isAppUsing('cacheLoginOption')" :methods="samlLoginMethods" class="mb-2" />
        <div v-else class="flex flex-col space-y-2 mb-2">
          <AuthLoginSAMLButton
            v-for="method in samlLoginMethods"
            :key="method.name"
            :text="continueWithSamlText(method.displayName)"
            :logo="method.logo"
            data-testid="samlLoginButton"
            @click="navigateTo(method.url)"
          />
        </div>
      </template>

      <template v-if="isAppUsing('cacheLoginOption')">
        <CachedAuthLoginThirdPartyGroupButtons
          v-if="xUsernameVerificationScreen && !!Object.keys(thirdPartyAuthLoginMethods).length"
          :login-methods="thirdPartyAuthLoginMethods"
          :type="AuthLoginThirdPartyGroupButtonsType.LogIn"
          class="mb-5"
        />
        <CachedAuthLoginThirdPartyGroupButtons
          v-else-if="!xUsernameVerificationScreen && !!Object.keys(userThirdPartyAuthLoginMethods).length"
          :show-more-dropdown="false"
          :login-methods="userThirdPartyAuthLoginMethods"
          :type="AuthLoginThirdPartyGroupButtonsType.Continue"
          class="mb-5"
        />
      </template>

      <template v-else>
        <AuthLoginThirdPartyGroupButtons
          v-if="xUsernameVerificationScreen && !!Object.keys(thirdPartyAuthLoginMethods).length"
          :login-methods="thirdPartyAuthLoginMethods"
          :type="AuthLoginThirdPartyGroupButtonsType.LogIn"
          class="mb-5"
        />

        <AuthLoginThirdPartyGroupButtons
          v-else-if="!xUsernameVerificationScreen && !!Object.keys(userThirdPartyAuthLoginMethods).length"
          :show-more-dropdown="false"
          :login-methods="userThirdPartyAuthLoginMethods"
          :type="AuthLoginThirdPartyGroupButtonsType.Continue"
          class="mb-5"
        />
      </template>

      <form
        novalidate
        :class="['flex flex-col mb-6', { 'tablet-portrait:mb-[72px]': xUsernameVerificationScreen }]"
        @submit.prevent="submit"
      >
        <!--
          Use `model-value` and `@input` here instead of `v-model`
          because the input value doesn't get updated on every keypress on android when using `v-model`
          @see https://github.com/vuejs/vue/issues/9777#issuecomment-478831263
        -->

        <!-- Display the email input on the email verification screen -->
        <OzLabelInput v-show="xUsernameVerificationScreen && isPasswordLoginEnabled" class="mb-4">
          <template #label>
            <label
              for="username-input"
              :class="['text-body-small-posts', 'text-dark-text-100 dark:text-light-text-100', 'px-2']"
            >
              {{ __('Or, log in with email/username') }}
            </label>
          </template>

          <template #input>
            <OzInput
              id="username-input"
              ref="usernameInputRef"
              name="username"
              autocomplete="email"
              type="email"
              test-id="usernameInput"
              :disabled="isAwaitingServerResponse"
              :model-value="username"
              :validation-message="usernameValidationMessage"
              placeholder="harrypotter@hogwarts.edu"
              :valid="isUsernameValid"
              :required="true"
              class="bg-light-ui-100 dark:bg-dark-ui-100"
              @input="onInputUsernameInput"
              @focusin="onFocusInUsernameInput"
              @blur="onBlurUsernameInput"
            />
          </template>
        </OzLabelInput>

        <!-- Display the password input on the login with password screen -->
        <OzLabelInput v-show="!xUsernameVerificationScreen && isPasswordLoginEnabled" class="mb-4">
          <template #label>
            <label
              for="password-input"
              :class="['text-body-small-posts', 'text-dark-text-100 dark:text-light-text-100', 'px-2']"
            >
              {{ Object.keys(userThirdPartyAuthLoginMethods).length ? __('Or enter password') : __('Enter password') }}
            </label>
          </template>

          <template #inputAccessory>
            <OzCheckBox
              :value="isPasswordShown"
              :label="__('Show')"
              :aria-label="__('Show password as plain text. Warning: this will display your password on the screen.')"
              :dir="dir()"
              class="px-2"
              @input="isPasswordShown = !isPasswordShown"
            />
          </template>

          <template #input>
            <OzInput
              id="password-input"
              ref="passwordInputRef"
              name="password"
              autocomplete="current-password"
              autocapitalize="none"
              :type="isPasswordShown ? 'text' : 'password'"
              test-id="passwordInput"
              :disabled="isAwaitingServerResponse"
              :model-value="password"
              :validation-message="passwordValidationMessage"
              :placeholder="__('Enter password')"
              :valid="isPasswordValid"
              :required="true"
              class="bg-light-ui-100 dark:bg-dark-ui-100"
              @input="onInputPasswordInput"
              @focusin="onFocusInPasswordInput"
              @blur="onBlurPasswordInput"
            />
          </template>
        </OzLabelInput>

        <div class="flex justify-center tablet-portrait:justify-end">
          <!-- Display the Continue button on the email verification screen -->
          <OzContainedButton
            v-if="xUsernameVerificationScreen"
            data-testid="continueButton"
            data-pw="continueButton"
            :color-scheme="OzContainedButtonColorScheme.Primary"
            :disabled="isAwaitingServerResponse || isContinueButtonDisabled"
            class="w-full tablet-portrait:w-auto"
          >
            {{ __('Continue') }}
          </OzContainedButton>

          <!-- Display the Log in button on the login with password screen -->
          <template v-else-if="isPasswordLoginEnabled">
            <OzLoadingText
              v-if="isAwaitingServerResponse"
              :text="__('Logging in...')"
              class="text-heading-4 text-center font-semibold px-5 py-2"
            />
            <OzContainedButton
              v-else
              data-testid="loginButton"
              data-pw="loginButton"
              :color-scheme="OzContainedButtonColorScheme.Primary"
              :disabled="isLoginButtonDisabled"
              class="w-full tablet-portrait:w-auto"
            >
              {{ __('Log in') }}
            </OzContainedButton>
          </template>
        </div>
      </form>

      <p
        class="text-body-small-posts text-dark-text-200 dark:text-light-text-200 text-center tablet-portrait:text-start px-2 tablet-portrait:mb-4"
      >
        <!-- Display "Backpack or Briefcase user? Log in here" on the email verification screen -->
        <template v-if="xUsernameVerificationScreen && !referredFromCreateSchoolLibrary && !isSchoolLibraryLoginPage">
          <span>{{ __('Backpack or Briefcase user?') }}</span>
          <OzTextLink :href="orgDomainVerificationPageUrl" :text="__('Log in here')" class="font-semibold ms-1" />
        </template>

        <!-- Display "Forgot password?" on the login with password screen -->
        <template v-else-if="isPasswordLoginEnabled">
          <OzTextLink
            :href="forgotPasswordUrl"
            :text="__('Forgot password?')"
            class="font-semibold"
            data-testid="forgotPasswordLink"
          />
        </template>
      </p>

      <AuthActionMenu v-if="xActionMenu" @cancel="xActionMenu = false" />
      <GlobalSnackbar />
    </main>

    <AuthBottomNav v-if="device.desktop" class="fixed start-6 bottom-6 hidden tablet-portrait:flex" />
  </div>
</template>

<style lang="postcss">
@import '@@/styles/4/kit';
@import '@@/styles/4/dot_grid';

* {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

html {
  @extend .dot-grid;
  background-position: start top;
  @apply h-screen;

  font-size: 16px;
}

@media screen and (orientation: landscape) {
  html {
    @apply overflow-auto;
  }
}
</style>
